import { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

// CONSTANTS
import { expandableNavLink, USER_ROLES } from 'constants/common';

// HOOKS
import { useAbility } from 'permissions';

// SELECTORS
import { selectUser } from 'services/Authentication/selectors';

// UTILS
import { historyPush } from 'utils/historyPush.utils';

// STYLES
import colors from 'styles/colors';
import {
  SideMenuContainer,
  NavItemsContainer,
  ExpandableTitle,
  ExpandableItems,
  NavItem,
  Icon,
} from './SideMenu.styles';

// Used for dropdown in the settings navigation links
const NavExpandable = (props) => {
  const navLinks = props.items.map((item, key) => {
    const navKey = `${key}-${item.translationId}`;
    return (
      <NavItem key={navKey} to={item.link} activeStyle={{ color: colors.primary }} exact>
        <FormattedMessage id={item.translationId} />
      </NavItem>
    );
  });

  const icon = !props.expandableOpen ? 'icon-caret-up-light' : 'icon-caret-down-light';

  const handleOpenExpandable = () => {
    props.onExpandableClick();
  };

  return (
    <div>
      <ExpandableTitle onClick={handleOpenExpandable}>
        <FormattedMessage id={props.title} />
        <Icon className={icon} />
      </ExpandableTitle>
      <ExpandableItems visible={props.expandableOpen}>{navLinks}</ExpandableItems>
    </div>
  );
};

// Used for the settings navigation links
const NavItems = (props) => {
  const { expandableOpen, onExpandableClick } = props;
  const ability = useAbility();
  const history = useHistory();
  const user = useSelector(selectUser());

  let firstEnabledNavlink = '';

  const navLinks = props.items?.map((item, key) => {
    const enabled = ability.can('view', 'settings', item.name);
    if (firstEnabledNavlink === '' && enabled) {
      firstEnabledNavlink = item.link;
    }

    const navKey = `${key}-${item.translationId}`;
    if (typeof item.link === 'object') {
      return (
        <NavExpandable
          key={navKey}
          title={item.translationId}
          expandableOpen={expandableOpen}
          items={item.link}
          onExpandableClick={onExpandableClick}
        />
      );
    }

    return (
      <NavItem
        key={navKey}
        disabled={!enabled}
        to={item.link}
        activeStyle={{
          color: colors.fleetBlue,
          borderColor: colors.selectorBorder,
          borderStyle: 'solid',
          borderWidth: '1px',
          borderRadius: '4px',
          backgroundColor: colors.white,
        }}
        exact
      >
        <FormattedMessage id={item.translationId} />
      </NavItem>
    );
  });

  useEffect(() => {
    if (
      user?.profile &&
      user?.profile.role !== USER_ROLES.admin &&
      firstEnabledNavlink !== '' &&
      history.location.pathname !== firstEnabledNavlink
    ) {
      historyPush(history, firstEnabledNavlink);
    }
  }, [firstEnabledNavlink, history, user]);

  return <NavItemsContainer>{navLinks}</NavItemsContainer>;
};

const SideMenu = (props) => {
  const location = useLocation();

  const [expandableOpen, setExpandableOpen] = useState(expandableNavLink.includes(location.pathname));

  return (
    <SideMenuContainer>
      <NavItems
        items={props.items}
        expandableOpen={expandableOpen}
        onExpandableClick={() => setExpandableOpen(!expandableOpen)}
      />
    </SideMenuContainer>
  );
};

export default SideMenu;
